// Security and Compliance Services for Payment Automation
// Browser-safe implementation with Web Crypto API fallbacks

const isBrowser =
  typeof window !== "undefined" && typeof window.document !== "undefined";

// Helper: Browser-safe random bytes
function getRandomBytes(size: number): Uint8Array {
  if (isBrowser && window.crypto && window.crypto.getRandomValues) {
    return window.crypto.getRandomValues(new Uint8Array(size));
  }
  // Fallback: generate pseudo-random (not cryptographically secure)
  const arr = new Uint8Array(size);
  for (let i = 0; i < size; i++) {
    arr[i] = Math.floor(Math.random() * 256);
  }
  return arr;
}

// Helper: Browser-safe SHA-256 hash
async function hashData(data: string): Promise<string> {
  if (isBrowser && window.crypto && window.crypto.subtle) {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", dataBuffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
  // Simple fallback hash (not cryptographically secure)
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(64, "0");
}

// Helper: Simple base64 encoding for demo encryption
// In production, use proper AES-GCM with key management
function simpleEncryptBase64(data: string): {
  encryptedData: string;
  iv: string;
} {
  const iv = Array.from(getRandomBytes(16))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return {
    encryptedData: btoa(data),
    iv,
  };
}

function simpleDecryptBase64(encryptedData: string): string {
  try {
    return atob(encryptedData);
  } catch {
    return "";
  }
}

export interface AuditLogEntry {
  id: string;
  timestamp: Date;
  userId?: string;
  action: string;
  resource: string;
  outcome: "success" | "failure" | "partial";
  details: any;
  ipAddress?: string;
  userAgent?: string;
  geoLocation?: {
    country: string;
    city: string;
    lat: number;
    lon: number;
  };
  dataClassification:
    | "public"
    | "internal"
    | "confidential"
    | "restricted"
    | "personalInfo";
  retentionDays: number;
}

export interface EncryptionResult {
  encryptedData: string;
  iv: string;
  keyId: string;
  algorithm: string;
}

export interface DataRetentionPolicy {
  dataTypes: {
    paymentData: number; // days
    personalInfo: number; // days
    auditLogs: number; // days
    ocrResults: number; // days
  };
  anonymizationRules: {
    phoneNumbers: boolean;
    emailAddresses: boolean;
    fullNames: boolean;
    addresses: boolean;
  };
}

export class SecurityComplianceManager {
  private encryptionKey: string;
  private auditLogs: AuditLogEntry[] = [];
  private dataRetentionPolicy: DataRetentionPolicy = {
    dataTypes: {
      paymentData: 2555, // 7 years (Thai banking requirement)
      personalInfo: 1095, // 3 years
      auditLogs: 2555, // 7 years
      ocrResults: 365, // 1 year
    },
    anonymizationRules: {
      phoneNumbers: true,
      emailAddresses: true,
      fullNames: true,
      addresses: true,
    },
  };

  constructor() {
    // In production, this would be loaded from secure key management
    // For browser compatibility, use localStorage or generate
    if (isBrowser) {
      const storedKey = localStorage.getItem("encryption_key");
      const generatedKey = this.generateEncryptionKey();
      this.encryptionKey = storedKey || generatedKey;
      if (!storedKey) {
        localStorage.setItem("encryption_key", this.encryptionKey);
      }
    } else {
      this.encryptionKey = this.generateEncryptionKey();
    }
  }

  // Data Encryption (browser-safe)
  encryptSensitiveData(
    data: string,
    classification: "confidential" | "restricted" = "confidential",
  ): EncryptionResult {
    const iv = Array.from(getRandomBytes(16))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    const encryptedBase64 = btoa(data); // Simple base64 for demo
    return {
      encryptedData: encryptedBase64,
      iv,
      keyId: "default-key-1",
      algorithm: "base64-demo",
    };
  }

  decryptSensitiveData(encryptedResult: EncryptionResult): string {
    try {
      return atob(encryptedResult.encryptedData);
    } catch {
      return "";
    }
  }

  // Audit Logging
  logAuditEvent(entry: Omit<AuditLogEntry, "id" | "timestamp">): void {
    const auditEntry: AuditLogEntry = {
      ...entry,
      id: this.generateAuditId(),
      timestamp: new Date(),
    };

    // Add to in-memory logs
    this.auditLogs.push(auditEntry);

    // Log to console for development
    console.log('Audit log:', {
      action: auditEntry.action,
      resource: auditEntry.resource,
      outcome: auditEntry.outcome,
      userId: auditEntry.userId,
    });

    // Check retention policy
    this.enforceRetentionPolicy();
  }

  private generateAuditId(): string {
    return `audit_${Date.now()}_${Array.from(getRandomBytes(8))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")}`;
  }

  // Data Anonymization
  anonymizePersonalData(data: any): any {
    if (typeof data !== "object" || data === null) {
      return data;
    }

    const anonymized = { ...data };

    if (
      this.dataRetentionPolicy.anonymizationRules.phoneNumbers &&
      anonymized.phone
    ) {
      anonymized.phone = this.anonymizePhoneNumber(anonymized.phone);
    }

    if (
      this.dataRetentionPolicy.anonymizationRules.emailAddresses &&
      anonymized.email
    ) {
      anonymized.email = this.anonymizeEmail(anonymized.email);
    }

    if (
      this.dataRetentionPolicy.anonymizationRules.fullNames &&
      anonymized.name
    ) {
      anonymized.name = this.anonymizeName(anonymized.name);
    }

    return anonymized;
  }

  private anonymizePhoneNumber(phone: string): string {
    if (phone.length <= 5) return "***";
    return (
      phone.substring(0, 3) +
      "*".repeat(phone.length - 5) +
      phone.substring(phone.length - 2)
    );
  }

  private anonymizeEmail(email: string): string {
    const [username, domain] = email.split("@");
    if (username.length <= 2) return `*@${domain}`;
    return (
      username.substring(0, 2) + "*".repeat(username.length - 2) + "@" + domain
    );
  }

  private anonymizeName(name: string): string {
    if (name.length <= 2) return "**";
    return (
      name.charAt(0) +
      "*".repeat(name.length - 2) +
      name.charAt(name.length - 1)
    );
  }

  // Data Retention
  private enforceRetentionPolicy(): void {
    const now = new Date();

    this.auditLogs = this.auditLogs.filter((log) => {
      const expired = Object.entries(this.dataRetentionPolicy.dataTypes).some(
        ([_, retentionDays]) => {
          const cutoff = new Date(
            now.getTime() - retentionDays * 24 * 60 * 60 * 1000,
          );
          return log.timestamp < cutoff;
        },
      );
      return !expired;
    });
  }

  // Compliance Validation
  validateGDPRCompliance(data: any): { compliant: boolean; issues: string[] } {
    const issues: string[] = [];

    if (!data.consentTimestamp) {
      issues.push("Missing consent timestamp");
    }

    if (data.collectedData && Object.keys(data.collectedData).length > 5) {
      issues.push(
        "Excessive data collection - may violate data minimization principle",
      );
    }

    if (!data.retentionPeriod || data.retentionPeriod > 2555) {
      issues.push("Retention period exceeds maximum allowed duration");
    }

    return {
      compliant: issues.length === 0,
      issues,
    };
  }

  validatePCICompliance(paymentData: any): {
    compliant: boolean;
    issues: string[];
  } {
    const issues: string[] = [];

    if (paymentData.cardNumber && !paymentData.cardNumberEncrypted) {
      issues.push("Card number must be encrypted at rest");
    }

    if (!paymentData.transmissionSecure) {
      issues.push("Payment data must be transmitted over secure channels");
    }

    if (!paymentData.accessControlled) {
      issues.push("Payment data must have strict access controls");
    }

    return {
      compliant: issues.length === 0,
      issues,
    };
  }

  // Geo-compliance
  validateRegionalCompliance(
    geoLocation: { country: string; city: string },
    action: string,
  ): { compliant: boolean; restrictions: string[] } {
    const restrictions: string[] = [];
    const country = geoLocation.country.toUpperCase();

    if (country === "THAILAND" || country === "TH") {
      if (action.includes("payment") && !this.hasThaiBankingLicense()) {
        restrictions.push("Payment processing requires Thai banking license");
      }
    }

    if (this.isEUCountry(country)) {
      restrictions.push("GDPR consent requirements apply");
    }

    if (country === "UNITED STATES" || country === "US") {
      restrictions.push("CCPA compliance requirements apply");
    }

    return {
      compliant: restrictions.length === 0,
      restrictions,
    };
  }

  private hasThaiBankingLicense(): boolean {
    return (
      (isBrowser
        ? false
        : process &&
          process.env &&
          process.env.THAI_BANKING_LICENSE === "active") || false
    );
  }

  private isEUCountry(country: string): boolean {
    const euCountries = [
      "AT",
      "BE",
      "BG",
      "HR",
      "CY",
      "CZ",
      "DK",
      "EE",
      "FI",
      "FR",
      "DE",
      "GR",
      "HU",
      "IE",
      "IT",
      "LV",
      "LT",
      "LU",
      "MT",
      "NL",
      "PL",
      "PT",
      "RO",
      "SK",
      "SI",
      "ES",
      "SE",
    ];
    return euCountries.includes(country);
  }

  // Monitoring and Reporting
  generateComplianceReport(): {
    totalAuditLogs: number;
    dataRetentionStatus: { [key: string]: number };
    complianceIssues: string[];
    lastAuditLog: Date | null;
  } {
    return {
      totalAuditLogs: this.auditLogs.length,
      dataRetentionStatus: this.dataRetentionPolicy.dataTypes,
      complianceIssues: [],
      lastAuditLog:
        this.auditLogs.length > 0
          ? this.auditLogs[this.auditLogs.length - 1].timestamp
          : null,
    };
  }

  // Utility methods (browser-safe)
  private generateEncryptionKey(): string {
    const bytes = getRandomBytes(32);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  hashSensitiveData(data: string): string {
    // Simplified sync hash for demo (not production)
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      hash = (hash << 5) - hash + data.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(64, "0");
  }

  generateSecureToken(length: number = 32): string {
    const bytes = getRandomBytes(length);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  validateDataIntegrity(data: string, expectedHash: string): boolean {
    // For demo: simple hash comparison
    const dataHash = this.hashSensitiveData(data);
    return dataHash === expectedHash;
  }
}

// Singleton instance
export const securityCompliance = new SecurityComplianceManager();
